import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 380;
const MARGIN = { top: 30, right: 30, bottom: 55, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 12,
    marginTop: 16,
  },
  summaryCard: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px 14px',
  },
  summaryLabel: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '4px',
  },
  summaryDate: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '6px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#374151',
    marginBottom: '2px',
  },
  badge: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    marginTop: '4px',
  },
};

const PRE_COLOR = '#2563eb';
const POST_COLOR = '#8b5cf6';
const CUTOFF_COLOR = '#ef4444';

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function formatFieldName(field) {
  const map = {
    pest365: 'Pest 365',
    pest365Plus: 'Pest 365 Plus',
    initialServiceVisit: 'Initial Service Visit',
    oneTime: 'One-Time',
    mosquitoTickControl: 'Mosquito & Tick Control',
  };
  return map[field] || field;
}

export default function RegressionDiscontinuityChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.observations || data.observations.length === 0 || !data.cutoffs || data.cutoffs.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(700, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const observations = data.observations;
    const cutoffs = data.cutoffs;

    // Build date and price domains
    const allDates = observations.map((d) => new Date(d.date));
    const allValues = observations.map((d) => d.value);

    // Include regression line endpoints in domain
    cutoffs.forEach((c) => {
      if (c.preBefore && c.preBefore.points) {
        c.preBefore.points.forEach((p) => allValues.push(p.y));
      }
      if (c.postAfter && c.postAfter.points) {
        c.postAfter.points.forEach((p) => allValues.push(p.y));
      }
    });

    const priceMin = d3.min(allValues);
    const priceMax = d3.max(allValues);
    const pricePadding = (priceMax - priceMin) * 0.15 || 1;

    // Scales
    const x = d3.scaleTime()
      .domain(d3.extent(allDates))
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Index scale: map observation index to x position via date
    const indexToX = (idx) => {
      const obs = observations.find((o) => o.index === idx);
      if (obs) return x(new Date(obs.date));
      // Interpolate if index is between known observations
      const minIdx = d3.min(observations, (o) => o.index);
      const maxIdx = d3.max(observations, (o) => o.index);
      const t = (idx - minIdx) / (maxIdx - minIdx);
      return t * innerW;
    };

    // Grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(observations.length, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => `$${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text(formatFieldName(data.field || 'pest365'));

    // Process each cutoff
    cutoffs.forEach((cutoff) => {
      const cutoffDate = new Date(cutoff.cutoffDate);
      const cx = x(cutoffDate);

      // -- Confidence bands around regression lines --
      const bandWidth = 0.8; // visual half-width in $ for band illustration

      // Pre-cutoff confidence band
      if (cutoff.preBefore && cutoff.preBefore.points && cutoff.preBefore.points.length >= 2) {
        const prePoints = cutoff.preBefore.points;
        const preAreaData = prePoints.map((p) => ({
          px: indexToX(p.x),
          upper: p.y + bandWidth,
          lower: p.y - bandWidth,
        }));

        const preArea = d3.area()
          .x((d) => d.px)
          .y0((d) => y(d.lower))
          .y1((d) => y(d.upper))
          .curve(d3.curveLinear);

        g.append('path')
          .datum(preAreaData)
          .attr('d', preArea)
          .attr('fill', PRE_COLOR)
          .attr('fill-opacity', 0.08)
          .attr('stroke', 'none');
      }

      // Post-cutoff confidence band
      if (cutoff.postAfter && cutoff.postAfter.points && cutoff.postAfter.points.length >= 2) {
        const postPoints = cutoff.postAfter.points;
        const postAreaData = postPoints.map((p) => ({
          px: indexToX(p.x),
          upper: p.y + bandWidth,
          lower: p.y - bandWidth,
        }));

        const postArea = d3.area()
          .x((d) => d.px)
          .y0((d) => y(d.lower))
          .y1((d) => y(d.upper))
          .curve(d3.curveLinear);

        g.append('path')
          .datum(postAreaData)
          .attr('d', postArea)
          .attr('fill', POST_COLOR)
          .attr('fill-opacity', 0.08)
          .attr('stroke', 'none');
      }

      // -- Pre-cutoff regression line --
      if (cutoff.preBefore && cutoff.preBefore.points && cutoff.preBefore.points.length >= 2) {
        const prePoints = cutoff.preBefore.points;
        const preLine = d3.line()
          .x((p) => indexToX(p.x))
          .y((p) => y(p.y))
          .curve(d3.curveLinear);

        g.append('path')
          .datum(prePoints)
          .attr('d', preLine)
          .attr('fill', 'none')
          .attr('stroke', PRE_COLOR)
          .attr('stroke-width', 2.5)
          .attr('stroke-opacity', 0.85);
      }

      // -- Post-cutoff regression line --
      if (cutoff.postAfter && cutoff.postAfter.points && cutoff.postAfter.points.length >= 2) {
        const postPoints = cutoff.postAfter.points;
        const postLine = d3.line()
          .x((p) => indexToX(p.x))
          .y((p) => y(p.y))
          .curve(d3.curveLinear);

        g.append('path')
          .datum(postPoints)
          .attr('d', postLine)
          .attr('fill', 'none')
          .attr('stroke', POST_COLOR)
          .attr('stroke-width', 2.5)
          .attr('stroke-opacity', 0.85);
      }

      // -- Vertical dashed cutoff line --
      g.append('line')
        .attr('x1', cx)
        .attr('x2', cx)
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', CUTOFF_COLOR)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4')
        .attr('stroke-opacity', 0.8);

      // Cutoff label at top
      g.append('text')
        .attr('x', cx)
        .attr('y', -8)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', CUTOFF_COLOR)
        .text(cutoff.label || 'Cutoff');

      // -- Discontinuity gap annotation (arrow/bracket) --
      if (cutoff.preBefore && cutoff.preBefore.points && cutoff.postAfter && cutoff.postAfter.points) {
        const preEndPoint = cutoff.preBefore.points[cutoff.preBefore.points.length - 1];
        const postStartPoint = cutoff.postAfter.points[0];

        const preEndY = y(preEndPoint.y);
        const postStartY = y(postStartPoint.y);

        const bracketX = cx + 14;

        // Vertical bracket line
        g.append('line')
          .attr('x1', bracketX)
          .attr('x2', bracketX)
          .attr('y1', preEndY)
          .attr('y2', postStartY)
          .attr('stroke', '#374151')
          .attr('stroke-width', 1.5);

        // Top tick of bracket
        g.append('line')
          .attr('x1', bracketX - 4)
          .attr('x2', bracketX + 4)
          .attr('y1', Math.min(preEndY, postStartY))
          .attr('y2', Math.min(preEndY, postStartY))
          .attr('stroke', '#374151')
          .attr('stroke-width', 1.5);

        // Bottom tick of bracket
        g.append('line')
          .attr('x1', bracketX - 4)
          .attr('x2', bracketX + 4)
          .attr('y1', Math.max(preEndY, postStartY))
          .attr('y2', Math.max(preEndY, postStartY))
          .attr('stroke', '#374151')
          .attr('stroke-width', 1.5);

        // Arrow pointing to the gap direction
        const midY = (preEndY + postStartY) / 2;
        const arrowDir = postStartPoint.y > preEndPoint.y ? -1 : 1;
        g.append('path')
          .attr('d', `M${bracketX},${midY} l${-4},${arrowDir * 5} l${8},0 Z`)
          .attr('fill', '#374151');

        // Gap magnitude label
        const sign = cutoff.discontinuity >= 0 ? '+' : '';
        g.append('text')
          .attr('x', bracketX + 8)
          .attr('y', midY + 4)
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', '#374151')
          .text(`${sign}$${cutoff.discontinuity.toFixed(2)}`);
      }

      // -- Significance badge on chart --
      const badgeY = 36;
      const badgeColor = cutoff.significant ? '#16a34a' : '#f59e0b';
      const badgeText = cutoff.significant
        ? `p=${cutoff.pValue.toFixed(3)} *`
        : `p=${cutoff.pValue.toFixed(3)}`;
      const badgeW = badgeText.length * 6.5 + 12;
      const badgeH = 18;

      g.append('rect')
        .attr('x', cx - badgeW / 2)
        .attr('y', badgeY - badgeH / 2)
        .attr('width', badgeW)
        .attr('height', badgeH)
        .attr('rx', 4)
        .attr('fill', badgeColor)
        .attr('fill-opacity', 0.12)
        .attr('stroke', badgeColor)
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.4);

      g.append('text')
        .attr('x', cx)
        .attr('y', badgeY + 3.5)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('font-weight', 700)
        .attr('fill', badgeColor)
        .text(badgeText);
    });

    // -- Observation scatter dots --
    g.selectAll('.obs-dot')
      .data(observations)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(new Date(d.date)))
      .attr('cy', (d) => y(d.value))
      .attr('r', 3.5)
      .attr('fill', '#fff')
      .attr('stroke', '#374151')
      .attr('stroke-width', 1.5);

    // -- Invisible hover targets for observations --
    g.selectAll('.obs-hover')
      .data(observations)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(new Date(d.date)))
      .attr('cy', (d) => y(d.value))
      .attr('r', 10)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mousemove', (event, d) => {
        // Find fitted value from the regression line at this index
        let fittedValue = null;
        for (const cutoff of cutoffs) {
          // Check pre-cutoff regression
          if (cutoff.preBefore && cutoff.preBefore.slope != null && d.index <= cutoff.cutoffIndex) {
            fittedValue = cutoff.preBefore.intercept + cutoff.preBefore.slope * d.index;
            break;
          }
          // Check post-cutoff regression
          if (cutoff.postAfter && cutoff.postAfter.slope != null && d.index >= cutoff.cutoffIndex) {
            fittedValue = cutoff.postAfter.intercept + cutoff.postAfter.slope * d.index;
            break;
          }
        }

        const residual = fittedValue != null ? d.value - fittedValue : null;

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          date: d.date,
          value: d.value,
          fittedValue,
          residual,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Handle window resize
  useEffect(() => {
    if (!data || !data.observations || data.observations.length === 0) return;

    const handleResize = () => {
      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();
      setTooltip(null);
    };

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [data]);

  // Empty state
  if (!data || !data.observations || data.observations.length === 0 || !data.cutoffs || data.cutoffs.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Regression Discontinuity Analysis</div>
          <div style={styles.subtitle}>Detecting sharp price changes at key events</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No regression discontinuity data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Regression Discontinuity Analysis</div>
        <div style={styles.subtitle}>Detecting sharp price changes at key events</div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <circle cx="5" cy="5" r="3.5" fill="#fff" stroke="#374151" strokeWidth="1.5" />
          </svg>
          <span>Observations</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={PRE_COLOR} strokeWidth="2.5" />
          </svg>
          <span>Pre-cutoff Fit</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={POST_COLOR} strokeWidth="2.5" />
          </svg>
          <span>Post-cutoff Fit</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill={PRE_COLOR} fillOpacity="0.08" rx="2" />
          </svg>
          <span>Confidence Band</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={CUTOFF_COLOR} strokeWidth="1.5" strokeDasharray="4,2" />
          </svg>
          <span>Cutoff</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Observed</span>
            <span style={{ fontWeight: 600 }}>${tooltip.value.toFixed(2)}</span>
          </div>
          {tooltip.fittedValue != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Fitted</span>
              <span style={{ fontWeight: 600, color: PRE_COLOR }}>${tooltip.fittedValue.toFixed(2)}</span>
            </div>
          )}
          {tooltip.residual != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Residual</span>
              <span style={{ fontWeight: 600, color: tooltip.residual >= 0 ? '#16a34a' : '#dc2626' }}>
                {tooltip.residual >= 0 ? '+' : ''}{tooltip.residual.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Summary cards for each cutoff */}
      {data.cutoffs.length > 0 && (
        <div style={styles.summaryGrid}>
          {data.cutoffs.map((cutoff, i) => {
            const isPositive = cutoff.discontinuity >= 0;
            const color = isPositive ? '#dc2626' : '#16a34a';
            const sigColor = cutoff.significant ? '#16a34a' : '#f59e0b';

            return (
              <div key={i} style={{ ...styles.summaryCard, borderLeft: `3px solid ${color}` }}>
                <div style={styles.summaryLabel}>Cutoff {i + 1}</div>
                <div style={styles.summaryDate}>{cutoff.label || cutoff.cutoffDate}</div>
                <div style={styles.summaryRow}>
                  <span>Date:</span>
                  <span style={{ fontWeight: 600 }}>{formatDateFull(cutoff.cutoffDate)}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Discontinuity:</span>
                  <span style={{ fontWeight: 600, color }}>
                    {isPositive ? '+' : ''}${cutoff.discontinuity.toFixed(2)} ({isPositive ? '+' : ''}{cutoff.discontinuityPercent.toFixed(2)}%)
                  </span>
                </div>
                <div style={styles.summaryRow}>
                  <span>p-value:</span>
                  <span style={{ fontWeight: 600 }}>{cutoff.pValue.toFixed(4)}</span>
                </div>
                <div>
                  <span
                    style={{
                      ...styles.badge,
                      color: sigColor,
                      background: sigColor + '15',
                    }}
                  >
                    {cutoff.significant ? 'Significant' : 'Not Significant'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
